// @greenlight-spec: 19-design-system.md
//
// The marketing site's "Northern lights" backdrop: palettes, the curtain's
// resting wander, and the two shader passes. `Aurora.tsx` owns the WebGL.

export type Rgb = [number, number, number];
export type AuroraTheme = 'light' | 'dark';

export interface AuroraSettings {
  colors: [Rgb, Rgb, Rgb, Rgb];
  accents: [Rgb, Rgb];
  intensity: number;
  contrast: number;
  brightness: number;
  saturation: number;
  grain: number;
  seed: number;
  timeScale: number;
}

const hex = (value: string): Rgb => {
  const n = parseInt(value.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
};

const PALETTES: Record<AuroraTheme, Omit<AuroraSettings, 'seed' | 'timeScale'>> = {
  dark: {
    colors: [hex('#121514'), hex('#1B1F1D'), hex('#1B7A4E'), hex('#56B881')],
    accents: [hex('#7C6CF5'), hex('#E07A9E')],
    intensity: 0.5,
    contrast: 0.9,
    brightness: -0.06,
    saturation: 0.8,
    grain: 0.184,
  },
  light: {
    colors: [hex('#FFFFFF'), hex('#D2EDDD'), hex('#8ED2AB'), hex('#56B881')],
    accents: [hex('#A99CF2'), hex('#EF9EBB')],
    intensity: 1.4,
    contrast: 1.012,
    brightness: 0,
    saturation: 1,
    grain: 0.105,
  },
};

/** The theme's palette, its darkest stop replaced by the page background so the canvas has no edge. */
export function auroraSettings(theme: AuroraTheme, background: Rgb | null): AuroraSettings {
  const p = PALETTES[theme];
  return {
    ...p,
    colors: [background ?? p.colors[0], p.colors[1], p.colors[2], p.colors[3]],
    seed: 7,
    timeScale: 0.8,
  };
}

/** A design token such as `0 0% 7%` (the form `globals.css` uses) as 0..1 RGB. */
export function hslTokenToRgb(token: string): Rgb | null {
  const m = /^\s*(-?[\d.]+)\s+([\d.]+)%\s+([\d.]+)%\s*$/.exec(token);
  if (!m) return null;
  const h = (((Number(m[1]) % 360) + 360) % 360) / 360;
  const s = Number(m[2]) / 100;
  const l = Number(m[3]) / 100;
  if (s === 0) return [l, l, l];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number): number => {
    const k = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
    if (k < 1 / 6) return p + (q - p) * 6 * k;
    if (k < 1 / 2) return q;
    if (k < 2 / 3) return p + (q - p) * (2 / 3 - k) * 6;
    return p;
  };
  return [channel(h + 1 / 3), channel(h), channel(h - 1 / 3)];
}

/** Where the curtain hangs, in shader units: (0, 0) is the centre and the short side spans 1. */
const HOME: [number, number] = [0.72, 0.3];

/** The moment a reduced-motion visitor sees, held still. */
export const REST_TIME = 10;

/**
 * The two curtains at `t` seconds as `[x, y, strength, phase] × 2`: the main
 * one wandering slowly near HOME, and a fainter one trailing it.
 */
export type CurtainSources = [number, number, number, number, number, number, number, number];

export function curtainSources(t: number, aspect: number): CurtainSources {
  const halfW = 0.5 * Math.max(aspect, 1);
  const l = t * 1.6;
  const x =
    (HOME[0] * halfW) / 0.89 -
    halfW * 0.32 * (0.5 + 0.5 * Math.sin(l * 0.08)) * (0.6 + 0.4 * Math.sin(l * 0.033 + 1.7));
  const y =
    HOME[1] - 0.1 * (0.5 + 0.5 * Math.sin(l * 0.07 + 0.9)) * (0.6 + 0.4 * Math.cos(l * 0.029));
  return [
    x,
    y,
    1.9,
    0,
    x - 0.42 + 0.1 * Math.sin(l * 0.05 + 2.3),
    y - 0.08 + 0.05 * Math.sin(l * 0.043 + 0.4),
    0.9,
    37,
  ];
}

export const VERTEX_SHADER =
  'attribute vec2 a_position; void main(){ gl_Position = vec4(a_position, 0.0, 1.0); }';

export const FIELD_SHADER = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform vec3 u_colors[4];
uniform vec3 u_accent[2];
uniform vec4 u_sources[2];
uniform vec2 u_resolution;
uniform float u_time;
uniform float u_spin;
uniform vec3 u_shape;   // intensity, seed, unused
uniform vec3 u_post;    // contrast, brightness, saturation

float hash21(vec2 p){ p = fract(p * vec2(234.34, 435.345)); p += dot(p, p + 34.23); return fract(p.x * p.y); }
float noise(vec2 p){
  vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash21(i), hash21(i + vec2(1.0, 0.0)), u.x), mix(hash21(i + vec2(0.0, 1.0)), hash21(i + vec2(1.0, 1.0)), u.x), u.y);
}
vec3 srgbToLinear(vec3 c){ return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(0.04045, c)); }
vec3 linearToSrgb(vec3 c){ return mix(c * 12.92, 1.055 * pow(max(c, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c)); }
vec3 linToOklab(vec3 c){
  float l = 0.4122214708 * c.r + 0.5363325363 * c.g + 0.0514459929 * c.b;
  float m = 0.2119034982 * c.r + 0.6806995451 * c.g + 0.1073969566 * c.b;
  float s = 0.0883024619 * c.r + 0.2817188376 * c.g + 0.6299787005 * c.b;
  l = pow(max(l, 0.0), 1.0 / 3.0); m = pow(max(m, 0.0), 1.0 / 3.0); s = pow(max(s, 0.0), 1.0 / 3.0);
  return vec3(0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s, 1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s, 0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s);
}
vec3 oklabToLin(vec3 c){
  float l = c.x + 0.3963377774 * c.y + 0.2158037573 * c.z;
  float m = c.x - 0.1055613458 * c.y - 0.0638541728 * c.z;
  float s = c.x - 0.0894841775 * c.y - 1.2914855480 * c.z;
  l = l * l * l; m = m * m * m; s = s * s * s;
  return vec3(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s, -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s, -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s);
}
vec3 mixColour(vec3 a, vec3 b, float t){
  return clamp(linearToSrgb(oklabToLin(mix(linToOklab(srgbToLinear(a)), linToOklab(srgbToLinear(b)), t))), 0.0, 1.0);
}
vec3 palette(float x){
  float f = clamp(x, 0.0, 1.0) * 3.0;
  vec3 col = u_colors[0];
  col = mixColour(col, u_colors[1], smoothstep(0.0, 1.0, clamp(f, 0.0, 1.0)));
  col = mixColour(col, u_colors[2], smoothstep(0.0, 1.0, clamp(f - 1.0, 0.0, 1.0)));
  col = mixColour(col, u_colors[3], smoothstep(0.0, 1.0, clamp(f - 2.0, 0.0, 1.0)));
  return col;
}

float curtain(vec2 p, float t, vec2 c, float morph, float x0, float seed, out float dyOut){
  float x = x0 + 0.7 * (noise(vec2(x0 * 1.2 + t * 0.1, morph * 0.5 + seed + 20.0)) - 0.5)
             + 0.35 * (noise(vec2(x0 * 3.0 - t * 0.08, morph + seed + 30.0)) - 0.5);
  float yc = c.y - 0.3 + 0.08 * (p.x - c.x) + 0.12 * sin(x * 1.5 + t * 0.16) + 0.08 * (noise(vec2(x * 0.9, morph * 0.4 + seed)) - 0.5);
  float dy = p.y - yc;
  float win = exp(-pow((p.x - c.x) / 0.55, 2.0)) * exp(-pow(max(dy - 0.12, 0.0) / 0.65, 2.0));
  float rx = x * 14.0;
  float beam = noise(vec2(rx, morph * 0.8 + seed + 3.0));
  float surge = noise(vec2(rx * 0.25 + 7.0, t * 0.5 + seed));
  float flare = pow(surge, 3.0) * 2.2;
  float rayH = 0.25 + 0.5 * beam * beam + 0.35 * flare;
  float body = smoothstep(-0.16, -0.02, dy) * exp(-max(dy, 0.0) * (1.6 / rayH));
  float c1 = noise(vec2(x * 1.6, morph + seed + 1.0));
  float c2 = noise(vec2(x * 4.0, morph * 0.7 + seed + 5.0));
  float c3 = noise(vec2(x * 9.0, morph * 1.3 + seed + 9.0));
  float folds = pow(clamp(c1 * 0.8 + c2 * 0.45 + c3 * 0.2, 0.0, 1.0), 1.5);
  float rays = 0.22 + 0.78 * pow(beam, 2.4);
  float shimmer = 0.9 + 0.1 * sin(dy * 18.0 - t * 0.6 + c2 * 4.0);
  float v = body * win * folds * rays * shimmer * (0.6 + 0.4 * flare);
  float dy2 = p.y - (yc + 0.22 + 0.06 * sin(x * 1.1 + t * 0.12));
  float body2 = smoothstep(-0.14, 0.0, dy2) * exp(-max(dy2, 0.0) * 2.6);
  float f2 = noise(vec2(x * 2.5 + 3.0, morph * 0.5 + seed + 2.0));
  float surge2 = noise(vec2(x * 3.5 + 11.0, t * 0.4 + seed + 5.0));
  v += 0.75 * body2 * win * f2 * f2 * (0.4 + 0.5 * surge2 * surge2);
  dyOut = dy;
  return v;
}

void main(){
  vec2 p = (gl_FragCoord.xy - 0.5 * u_resolution) / min(u_resolution.x, u_resolution.y);
  float seed = u_shape.y;
  float morph = u_spin * 0.8;
  float x = p.x * 1.6;
  float v = 0.0, dyAcc = 0.0, wAcc = 0.0;
  for (int i = 0; i < 2; i++) {
    float dy;
    float vi = curtain(p, u_time, u_sources[i].xy, morph + u_sources[i].w, x, seed, dy) * u_sources[i].z;
    v += vi; dyAcc += dy * vi; wAcc += vi;
  }
  float dyMean = wAcc > 0.0001 ? dyAcc / wAcc : 0.0;
  float hue = noise(vec2(x * 2.5 + 11.0, morph * 0.4 + seed));
  float lim = 0.78;
  v = 0.04 + lim * (1.0 - exp(-(0.6 + u_shape.x) * v * 1.5 / lim));

  vec3 col = palette(v);
  float lit = smoothstep(0.04, 0.45, v);
  col = mixColour(col, u_accent[0], lit * smoothstep(0.2, 0.55, dyMean) * (0.15 + 0.3 * hue));
  col = mixColour(col, u_accent[1], lit * smoothstep(0.45, 0.8, dyMean) * (0.1 + 0.3 * (1.0 - hue)));

  col = (col - 0.5) * u_post.x + 0.5;
  col = mix(vec3(dot(col, vec3(0.299, 0.587, 0.114))), col, u_post.z);
  col += u_post.y;
  // Alpha is how much grain the finish pass lays here: the grain lives in the light.
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 0.12 + 0.88 * smoothstep(0.06, 0.45, v));
}`;

export const FINISH_SHADER = `
precision highp float;
uniform sampler2D u_field;
uniform vec2 u_size;
uniform vec2 u_grain;   // amount, seed
float grainHash(vec2 p){ vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
void main(){
  vec4 f = texture2D(u_field, gl_FragCoord.xy / u_size);
  float g = grainHash(floor(gl_FragCoord.xy) + vec2(u_grain.y * 17.0, u_grain.y * 31.0)) - 0.5;
  gl_FragColor = vec4(clamp(f.rgb + g * u_grain.x * f.a, 0.0, 1.0), 1.0);
}`;
